import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { IFinancingOption, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { HyperlinkTargetEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { Text } from '@inter-ikea-kompis/component-text';

/**
 * Financial services link.
 *
 * @event linkClick Triggered when the user clicks on the financial services button link.
 */

export default class FinancialServicesLink extends KompisElement {
	// The financing option retrieved by service.
	@property({ type: Object, required: true })
	public financingOption!: IFinancingOption;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	public override render(): RenderResult {
		return html`<kompis-text
			.theme="${this.theme}"
			.type="${ThemeFontStyleTypeEnum.bodyMedium}"
			.bold="${true}"
		>
			${this.getHyperLink()}
		</kompis-text>`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}
	private getHyperLink(): RenderResult {
		const { anchor, anchorLink } = this.financingOption;

		if (anchorLink && !this.disableLinks) {
			return html`<kompis-hyperlink
				.theme="${this.theme}"
				.text="${anchor}"
				.underline="${true}"
				.href="${anchorLink}"
				.target="${HyperlinkTargetEnum.blank}"
				@linkClick="${() =>
					this.dispatchEvent(
						new CustomEvent('linkClick', {
							bubbles: true,
							detail: { triggerElement: this.getTriggerElement() }
						})
					)}}"
			>
			</kompis-hyperlink>`;
		}

		return html`<kompis-hyperlink
			.theme="${this.theme}"
			.text="${anchor}"
			.underline="${true}"
			@linkClick="${() =>
				this.dispatchEvent(
					new CustomEvent('linkClick', {
						bubbles: true,
						detail: { triggerElement: this.getTriggerElement() }
					})
				)}}"
		>
		</kompis-hyperlink>`;
	}

	private getTriggerElement(): HTMLElement {
		const hyperLink = this.shadowRoot?.querySelector(
			this.customElements.getInternalName('kompis-hyperlink')
		);
		const focusOutline = hyperLink?.shadowRoot?.querySelector(
			hyperLink.customElements.getInternalName('kompis-focus-outline')
		);
		return <HTMLElement>focusOutline?.querySelector('a');
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-financial-services-link': FinancialServicesLink;
	}
}
